import React, { useState, useEffect } from "react";
import axiosInstance from "../../../api/axiosInstance";
import '../../../styles/modal.css';

// SVG Icons
const JiraIcon = ({ isSelected }) => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    width="24" 
    height="24" 
    viewBox="0 0 24 24" 
    fill={isSelected ? "#0052CC" : "#9CA3AF"}
  >
    <path d="M11.571 11.513H0a5.218 5.218 0 0 0 5.232 5.215h2.13v2.057A5.215 5.215 0 0 0 12.575 24V12.518a1.005 1.005 0 0 0-1.005-1.005z" />
    <path d="M5.01 11.513H16.58a5.215 5.215 0 0 0-5.232-5.215H9.22V4.243A5.216 5.216 0 0 0 4.004 0v11.513a1.005 1.005 0 0 0 1.005 0z" fill={isSelected ? "#2684FF" : "#D1D5DB"} />
  </svg>
);

const WorkXiqIcon = ({ isSelected }) => (
  <img
    src="/workxiq-logo.svg"
    alt="WorkXiq"
    width="24"
    height="24"
    style={{ objectFit: "contain", opacity: isSelected ? 1 : 0.5 }}
  />
);

const SourceSelector = ({ selectedSource, onSourceChange, isLoading }) => (
  <div className="flex gap-4 mb-6 pb-6 border-b border-gray-200 dark:border-gray-700">
    {[
      { id: "jira", label: "Jira", icon: JiraIcon },
      { id: "projectpulse", label: "WorkXiq", icon: WorkXiqIcon }
    ].map((source) => {
      const IconComponent = source.icon;
      const isSelected = selectedSource === source.id;
      
      return (
        <button
          key={source.id}
          onClick={() => onSourceChange(source.id)}
          disabled={isLoading}
          className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 ${
            isSelected
              ? "bg-blue-100 dark:bg-blue-900 border-2 border-blue-500 shadow-md"
              : "bg-gray-100 dark:bg-gray-800 border-2 border-transparent hover:border-gray-300 dark:hover:border-gray-600"
          } ${isLoading ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
        >
          <IconComponent isSelected={isSelected} />
          <span className={`font-medium text-sm ${
            isSelected 
              ? "text-blue-600 dark:text-blue-300" 
              : "text-gray-600 dark:text-gray-400"
          }`}>
            {source.label}
          </span>
          {isSelected && (
            <div className="ml-auto">
              <svg className="w-5 h-5 text-blue-600 dark:text-blue-300" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            </div>
          )}
        </button>
      );
    })}
  </div>
);

const LinkProjectModal = ({ isOpen, onClose, projectId, onLinkSuccess }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isLinking, setIsLinking] = useState(false);
  const [selectedSource, setSelectedSource] = useState("jira");
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setSelectedSource("jira");
      setProjects([]);
      setSelectedProject(null);
      setIsLoading(false);
      setIsLinking(false);
      setError(null);
      fetchSourceProjects("jira");
    }
  }, [isOpen]);

  const fetchSourceProjects = async (source) => {
    setIsLoading(true);
    setError(null);
    setProjects([]);
    setSelectedProject(null);

    try {
      const sourceParam = source === "jira" ? 1 : 2;
      const response = await axiosInstance.get(
        `/jira_data/get_projects?source=${sourceParam}`
      );

      // Handle different response structures based on source
      let fetchedProjects = [];
      if (source === "jira") {
        fetchedProjects = response?.projects || [];
      } else if (source === "projectpulse") {
        fetchedProjects = response?.projects || response.data?.projects || [];
      }

      setProjects(fetchedProjects);
    } catch (err) {
      const errorMessage =
        err.response?.data?.detail ||
        err.message ||
        `Failed to fetch ${source === "jira" ? "Jira" : "WorkXiq"} projects`;
      
      setError(errorMessage);
      setProjects([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSourceChange = (source) => {
    setSelectedSource(source);
    setSelectedProject(null);
    setError(null);
    fetchSourceProjects(source);
  };

  const handleProjectSelect = (project) => {
    setSelectedProject(project);
  };

  const handleLinkProject = async () => {
    if (!selectedProject) {
      setError("Please select a project");
      return;
    }

    setIsLinking(true);
    setError(null);

    try {
      const sourceParam = selectedSource === "jira" ? 1 : 2;
      const response = await axiosInstance.post(
        `/projects/link_project_to_source`,
        {
  "project_id": projectId,
  "integration_source": sourceParam,
  "source_project_id": selectedProject.id,
  "source_project_key": getProjectKey()
}
      );

      if (response  ?.status_code == 200) {
        onClose();
        if (onLinkSuccess) {
          onLinkSuccess();
        }
      }
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        err.response?.data?.detail ||
        err.message ||
        "Failed to link project";
      
      setError(errorMessage);
    } finally {
      setIsLinking(false);
    }
  };

  if (!isOpen) return null;

  const sourceLabels = {
    jira: "Jira",
    projectpulse: "WorkXiq"
  };

  const getProjectKey = () => {
    if (!selectedProject) return "";
    return selectedProject.key || selectedProject.identifier || "";
  };

  return (
    <div data-rt-modal-overlay className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={(e) => { if (e.target === e.currentTarget) { const b = e.currentTarget.querySelector('[data-rt-modal-close], .close-btn, .modal-close, button[aria-label="Close"], button[aria-label="close"]'); if (b) b.click(); } }}>
      <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto">
        {/* Modal Header */}
        <div className="sticky top-0 flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            Link Project to Source
          </h2>
          <button
            className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
            onClick={onClose}
            disabled={isLinking}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        {/* Modal Content */}
        <div className="p-6">
          {/* Source Selector */}
          <SourceSelector
            selectedSource={selectedSource}
            onSourceChange={handleSourceChange}
            isLoading={isLoading}
          />

          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="animate-spin">
                <svg
                  className="w-12 h-12 text-blue-500"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
              </div>
              <p className="mt-4 text-gray-600 dark:text-gray-400">
                Loading {sourceLabels[selectedSource]} projects...
              </p>
            </div>
          ) : error && !selectedProject ? (
            <div className="bg-red-50 dark:bg-red-900 border border-red-200 dark:border-red-700 rounded-lg p-4 mb-4">
              <div className="flex items-start gap-3">
                <svg
                  className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
                <p className="text-sm text-red-800 dark:text-red-200">
                  {error}
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Projects Dropdown */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Select Project
                </label>
                <div className="relative">
                  <select
                    value={selectedProject?.id || ""}
                    onChange={(e) => {
                      const project = projects.find(
                        (p) => p.id === e.target.value
                      );
                      handleProjectSelect(project);
                    }}
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                  >
                    <option value="">Choose a project...</option>
                    {projects.map((project) => (
                      <option key={project.id} value={project.id}>
                        {project.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Project Key Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Project Key
                </label>
                <input
                  type="text"
                  value={getProjectKey()}
                  disabled
                  className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white cursor-not-allowed opacity-60"
                  placeholder="Project key will appear here"
                />
              </div>

              {/* Error message for linking */}
              {error && selectedProject && (
                <div className="bg-red-50 dark:bg-red-900 border border-red-200 dark:border-red-700 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <svg
                      className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <p className="text-sm text-red-800 dark:text-red-200">
                      {error}
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="sticky bottom-0 flex justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
          <button
            type="button"
            onClick={onClose}
            disabled={isLoading || isLinking}
            className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleLinkProject}
            disabled={isLoading || isLinking || !selectedProject}
            className="px-6 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-700 text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
          >
            {isLinking ? (
              <>
                <svg className="w-4 h-4 animate-spin" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M11 3a1 1 0 10-2 0v1a1 1 0 102 0V3zM15.657 5.757a1 1 0 00-1.414-1.414l-.707.707a1 1 0 001.414 1.414l.707-.707zM18 10a1 1 0 01-1 1h-1a1 1 0 110-2h1a1 1 0 011 1zM15.657 14.243a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414l.707.707zM11 17a1 1 0 102 0v-1a1 1 0 10-2 0v1zM5.757 15.657a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414l-.707.707zM5 10a1 1 0 01-1-1V8a1 1 0 012 0v1a1 1 0 01-1 1zM5.757 5.757a1 1 0 000-1.414L5.05 5.05a1 1 0 10-1.414 1.414l.707.707z" />
                </svg>
                Linking...
              </>
            ) : (
              "Link Project"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LinkProjectModal;
